import { format } from 'date-fns';
import type { EpcProject, EpcProjectHealth, EpcProjectProgress } from '../../../../services/epcProjectService';

export interface DeriveInput {
  project: Pick<EpcProject, 'status' | 'startDate' | 'endDatePlanned' | 'progress'>;
  now?: Date;
}

export interface DeriveOutput {
  plannedPct: number; // 0-100
  actualPct: number | null; // null = unknown (no backend data)
  variancePct: number | null;
  health: EpcProjectHealth;
  overdueTaskCount: number;
  startDate: Date | null;
  endDate: Date | null;
  expectedEndDate: Date | null;
  reasonText: string;
  isFallback: boolean; // true when backend `progress` was absent
}

function parseDate(value: string | undefined | null): Date | null {
  if (value == null) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function clamp(min: number, max: number, value: number): number {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function pluralizeTasks(n: number): string {
  return n === 1 ? '1 overdue task' : `${n} overdue tasks`;
}

function buildReasonText(args: {
  health: EpcProjectHealth;
  variancePct: number | null;
  overdueTaskCount: number;
  startDate: Date | null;
  expectedEndDate: Date | null;
}): string {
  const { health, variancePct, overdueTaskCount, startDate, expectedEndDate } = args;
  switch (health) {
    case 'on_track':
      return 'On schedule';
    case 'completed':
      return 'Completed';
    case 'not_configured':
      return 'Schedule not set';
    case 'upcoming':
      return startDate ? `Starts ${format(startDate, 'MMM d')}` : 'Not started';
    case 'delayed':
      return expectedEndDate ? `Past planned end · expected ${format(expectedEndDate, 'MMM d')}` : 'Past planned end';
    case 'at_risk': {
      const hasVariance = variancePct !== null && variancePct < 0;
      const hasOverdue = overdueTaskCount > 0;
      if (hasVariance && hasOverdue) {
        return `Behind ${Math.abs(Math.round(variancePct as number))}% · ${pluralizeTasks(overdueTaskCount)}`;
      }
      if (hasOverdue) {
        return pluralizeTasks(overdueTaskCount);
      }
      if (hasVariance) {
        return `Behind ${Math.abs(Math.round(variancePct as number))}%`;
      }
      return 'At risk';
    }
    default:
      return '';
  }
}

function computeFallbackHealth(args: {
  status: string | undefined;
  startDate: Date | null;
  endDate: Date | null;
  now: Date;
}): EpcProjectHealth {
  const { status, startDate, endDate, now } = args;
  if (status === 'completed') return 'completed';
  if (!startDate || !endDate || endDate.getTime() <= startDate.getTime()) return 'not_configured';
  if (now.getTime() < startDate.getTime()) return 'upcoming';
  if (now.getTime() > endDate.getTime()) return 'delayed';
  return 'on_track';
}

function computeBackendHealth(args: {
  status: string | undefined;
  progress: EpcProjectProgress;
  startDate: Date | null;
  endDate: Date | null;
  expectedEndDate: Date | null;
  now: Date;
}): EpcProjectHealth {
  const { status, progress, startDate, endDate, expectedEndDate, now } = args;

  // Always promote to completed if status says so or actual >= 100.
  if (status === 'completed' || progress.actualPct >= 100) return 'completed';

  // Trust backend-supplied health for the in-between buckets when present.
  // Still apply conservative date-based overrides for not_configured/upcoming/delayed.
  if (!startDate || !endDate) return 'not_configured';
  if (now.getTime() < startDate.getTime()) return 'upcoming';
  const effectiveEnd = expectedEndDate ?? endDate;
  if (now.getTime() > effectiveEnd.getTime()) return 'delayed';

  if (progress.health === 'completed') {
    // Backend says completed but status/actual disagree; defer to status logic above.
    // Falls through to derived logic.
  } else if (
    progress.health === 'on_track' ||
    progress.health === 'at_risk' ||
    progress.health === 'delayed' ||
    progress.health === 'upcoming' ||
    progress.health === 'not_configured'
  ) {
    // Trust backend health unless we already overrode above.
    return progress.health;
  }

  // Derived fallback for at_risk vs on_track.
  if (progress.variancePct <= -10 || progress.overdueTaskCount > 0) return 'at_risk';
  return 'on_track';
}

export function deriveProjectProgress(input: DeriveInput): DeriveOutput {
  const now = input.now ?? new Date();
  const { project } = input;

  const startDate = parseDate(project.startDate);
  const endDate = parseDate(project.endDatePlanned);

  if (project.progress) {
    const progress = project.progress;
    const expectedEndDate = parseDate(progress.expectedEndDate);

    const health = computeBackendHealth({
      status: project.status,
      progress,
      startDate,
      endDate,
      expectedEndDate,
      now,
    });

    const reasonText = buildReasonText({
      health,
      variancePct: progress.variancePct,
      overdueTaskCount: progress.overdueTaskCount,
      startDate,
      expectedEndDate,
    });

    return {
      plannedPct: clamp(0, 100, progress.plannedPct),
      actualPct: clamp(0, 100, progress.actualPct),
      variancePct: progress.variancePct,
      health,
      overdueTaskCount: progress.overdueTaskCount,
      startDate,
      endDate,
      expectedEndDate,
      reasonText,
      isFallback: false,
    };
  }

  // Fallback path — no backend progress.
  const health = computeFallbackHealth({ status: project.status, startDate, endDate, now });

  let plannedPct = 0;
  if (startDate && endDate && endDate.getTime() > startDate.getTime()) {
    const total = endDate.getTime() - startDate.getTime();
    const elapsed = now.getTime() - startDate.getTime();
    plannedPct = clamp(0, 100, (elapsed / total) * 100);
  } else if (health === 'completed') {
    plannedPct = 100;
  }

  const reasonText = buildReasonText({
    health,
    variancePct: null,
    overdueTaskCount: 0,
    startDate,
    expectedEndDate: null,
  });

  return {
    plannedPct,
    actualPct: null,
    variancePct: null,
    health,
    overdueTaskCount: 0,
    startDate,
    endDate,
    expectedEndDate: null,
    reasonText,
    isFallback: true,
  };
}
